import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { routePath, codesPath } from "~/shared/constants";
import { SEMANTIC_SEARCH_PAGE_SIZE } from "~/hooks/search/constants";
import { Input } from "~/components/ui/input";
import { useAnalytics } from "~/contexts/analytics-context";
import {
  SEARCH_SUGGESTION_ANALYTICS,
  SEARCH_SUGGESTION_LIST,
} from "~/pages/home/constants";
import {
  SearchSuggestionType,
  type SearchSuggestion,
} from "~/pages/home/types";
import { SearchSuggestionList } from "./search-suggestion-list";

export const HomeSearch = () => {
  const { t } = useTranslation(["home"]);
  const navigate = useNavigate();
  const { gaEvent } = useAnalytics();
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [previousSearchInputValue, setPreviousSearchInputValue] = useState("");

  const suggestions = useMemo(() => {
    if (!searchInputValue) return [];
    return SEARCH_SUGGESTION_LIST(t, searchInputValue).flatMap((group) =>
      group.items.map((item) => ({ ...item, groupTitle: group.title })),
    );
  }, [t, searchInputValue]);

  if (searchInputValue !== previousSearchInputValue) {
    setPreviousSearchInputValue(searchInputValue);
    setSelectedIndex(-1);
  }

  const handleSuggestionSelect = useCallback(
    (type: string, searchQuery: string) => {
      gaEvent({
        type: "home_search_suggestion_selected",
        payload: {
          ...SEARCH_SUGGESTION_ANALYTICS[type as SearchSuggestionType],
          query: searchQuery,
        },
      });

      switch (type as SearchSuggestionType) {
        case SearchSuggestionType.BOOKS_QUICK:
          navigate({
            to: "/books",
            search: { q: searchQuery, type: "quick" },
            state: { from: routePath("/") },
          });
          break;
        case SearchSuggestionType.BOOKS_SEMANTIC:
          navigate({
            to: "/books",
            search: {
              q: searchQuery,
              type: "semantic",
              pageSize: String(SEMANTIC_SEARCH_PAGE_SIZE),
            },
            state: { from: routePath("/") },
          });
          break;
        case SearchSuggestionType.THEMA_QUICK:
          navigate({
            href: codesPath("THEMA", searchQuery),
            state: { from: routePath("/") },
          });
          break;
        case SearchSuggestionType.BISAC_QUICK:
          navigate({
            href: codesPath("BISAC", searchQuery),
            state: { from: routePath("/") },
          });
          break;
        default:
          break;
      }

      setSearchInputValue("");
    },
    [gaEvent, navigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchInputValue || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((i) => (i >= suggestions.length - 1 ? 0 : i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const item = suggestions[selectedIndex];
          handleSuggestionSelect(item.type, item.value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setSearchInputValue("");
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const groupedSuggestions = useMemo(() => {
    const groups = new Map<
      string,
      (SearchSuggestion & { groupTitle: string })[]
    >();
    suggestions.forEach((item) => {
      if (!groups.has(item.groupTitle)) {
        groups.set(item.groupTitle, []);
      }
      groups.get(item.groupTitle)!.push(item);
    });
    return Array.from(groups.entries());
  }, [suggestions]);

  return (
    <div className="w-full lg:w-2xl relative">
      <div className="relative w-full">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
        <Input
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full h-13 pl-12 text-md text-secondary-600 shadow-outline-home hover:border-primary-500 rounded-2xl"
          placeholder={t("home:search.placeholder")}
        />
      </div>
      <div>
        {searchInputValue && suggestions.length > 0 && (
          <div className="absolute top-14 z-10 w-full flex flex-col gap-y-2 rounded-xl py-[10px] border border-secondary-100 bg-popover">
            {groupedSuggestions.map(([groupTitle, items]) => (
              <SearchSuggestionList
                key={groupTitle}
                title={groupTitle}
                items={items}
                onItemClick={handleSuggestionSelect}
                selectedItem={suggestions[selectedIndex]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
